import React, { useState, useEffect } from "react";
import axios from "axios";
import { buildApiUrl, API_CONFIG } from "../config/api";
import SignaturePad from "./SignaturePad";
import jsPDF from "jspdf";

interface FormField {
  id: string;
  name: string;
  type: string;
  label: string;
  required?: boolean;
  order: number;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    minDate?: string;
    maxDate?: string;
  };
  options?: Array<{
    value: string;
    label: string;
  }>;
  description?: string;
  privacyLevel?: string;
}

interface FormSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  fields: FormField[];
  content?: {
    introduction?: string;
    purpose?: string;
    purposes?: string[];
    retention?: string;
    importance?: string;
    patientRights?: string[];
    rights?: string;
  };
  privacyNotice?: string;
  consentRequired?: boolean;
}

interface FormTemplate {
  formTemplate: {
    template_name: string;
    description: string;
    form_type: string;
    form_schema: {
      name: string;
      description: string;
      sections: FormSection[];
      metadata: {
        estimatedCompletionTime: string;
        requiredConsents?: string[];
        signatureRequired: boolean;
        parentGuardianRequired: boolean;
        privacyLevel?: string;
        retentionPeriod?: string;
        securityMeasures?: string[];
      };
    };
  };
  intakeToken: {
    patient_id: string;
    patient_date_of_birth: string;
    location_id: string;
    token: string;
  };
  s3UploadUrl: string;
  s3Key: string;
}

interface BrandingInfo {
  branding: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    logo: string;
    logoUrl: string;
    theme: string;
    fontFamily: string;
    customCSS: string;
    footerText: string;
    contactInfo: {
      phone: string;
      email: string;
      address: string;
    };
  };
  patientInfo: {
    patient_id: string;
    patient_date_of_birth: string;
    location_id: string;
  };
}

interface DynamicFormProps {
  token: string;
  formType: string;
  jwtToken: string;
  brandingInfo: BrandingInfo | null;
  onComplete: () => void;
}

const DynamicForm: React.FC<DynamicFormProps> = ({
  token,
  formType,
  jwtToken,
  brandingInfo,
  onComplete,
}) => {
  const [formData, setFormData] = useState<FormTemplate | null>(null);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [signature, setSignature] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchFormTemplate();
  }, [token, formType]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      // When user presses browser back button, redirect to form selection
      onComplete();
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if form has unsaved data
      const hasFormData = Object.values(formValues).some(
        (value) => value !== undefined && value !== null && value !== ""
      );

      if (hasFormData) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    // Add event listeners
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [onComplete, formValues]);

  const fetchFormTemplate = async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await axios.get(
        buildApiUrl(
          `${API_CONFIG.ENDPOINTS.INTAKE_FORM}/${token}?form_type=${formType}`
        )
      );

      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching form template:", error);
      setError("Failed to load form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (fieldName: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const generatePDF = (): Blob => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Add title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(formData?.formTemplate.template_name || "Form", 20, yPosition);
    yPosition += 15;

    // Add description
    if (formData?.formTemplate.description) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      const descriptionLines = doc.splitTextToSize(
        formData.formTemplate.description,
        pageWidth - 40
      );
      doc.text(descriptionLines, 20, yPosition);
      yPosition += descriptionLines.length * 5 + 10;
    }

    // Add form data
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Form Data:", 20, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    // Add form fields
    formData?.formTemplate.form_schema.sections
      .sort((a, b) => a.order - b.order)
      .forEach((section) => {
        // Add section title
        doc.setFont("helvetica", "bold");
        doc.text(`${section.title}:`, 20, yPosition);
        yPosition += 8;

        // Add section fields
        section.fields
          .sort((a, b) => a.order - b.order)
          .forEach((field) => {
            const value = formValues[field.name];
            if (value !== undefined && value !== null && value !== "") {
              const label = field.label;
              let displayValue = value;

              if (field.type === "checkbox") {
                displayValue = value ? "Yes" : "No";
              } else if (field.type === "date") {
                displayValue = new Date(value).toLocaleDateString();
              }

              const fieldText = `${label}: ${displayValue}`;
              const fieldLines = doc.splitTextToSize(fieldText, pageWidth - 40);
              doc.setFont("helvetica", "normal");
              doc.text(fieldLines, 30, yPosition);
              yPosition += fieldLines.length * 4 + 2;

              // Check if we need a new page
              if (yPosition > pageHeight - 20) {
                doc.addPage();
                yPosition = 20;
              }
            }
          });

        yPosition += 5;
      });

    // Add signature if present
    if (signature) {
      doc.setFont("helvetica", "bold");
      doc.text("Digital Signature:", 20, yPosition);
      yPosition += 10;

      // Add signature image
      try {
        doc.addImage(signature, "PNG", 20, yPosition, 100, 50);
        yPosition += 60;
      } catch (error) {
        doc.setFont("helvetica", "normal");
        doc.text("Signature captured", 20, yPosition);
        yPosition += 10;
      }
    }

    // Add metadata
    yPosition += 20;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, yPosition);
    doc.text(
      `Patient ID: ${formData?.intakeToken.patient_id}`,
      20,
      yPosition + 5
    );
    doc.text(
      `Location ID: ${formData?.intakeToken.location_id}`,
      20,
      yPosition + 10
    );

    return doc.output("blob");
  };

  const uploadToS3 = async (pdfBlob: Blob, s3Url: string): Promise<boolean> => {
    try {
      const response = await fetch(s3Url, {
        method: "PUT",
        body: pdfBlob,
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      if (response.ok) {
        console.log("PDF uploaded to S3 successfully");
        return true;
      } else {
        console.error("Failed to upload PDF to S3:", response.statusText);
        return false;
      }
    } catch (error) {
      console.error("Error uploading PDF to S3:", error);
      return false;
    }
  };

  const downloadPDF = (pdfBlob: Blob, filename: string) => {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateForm = (): boolean => {
    if (!formData) return false;

    const requiredFields = formData.formTemplate.form_schema.sections
      .flatMap((section) => section.fields)
      .filter((field) => field.required);

    for (const field of requiredFields) {
      if (!formValues[field.name] || formValues[field.name] === "") {
        setError(`Please fill in the required field: ${field.label}`);
        return false;
      }
    }

    // Check if signature is required and provided
    const requiresSignature =
      formData.formTemplate.form_schema.metadata?.signatureRequired;
    if (requiresSignature && !signature) {
      setError("Please provide your digital signature to complete the form.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      // Generate PDF
      console.log("Generating PDF...");
      const pdfBlob = generatePDF();

      // Create filename for PDF
      const timestamp = new Date().toISOString().split("T")[0];
      const formName = formData?.formTemplate.template_name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      const filename = `${formName}_${timestamp}.pdf`;

      // Upload to S3
      console.log("Uploading PDF to S3...");
      const s3UploadSuccess = await uploadToS3(
        pdfBlob,
        formData?.s3UploadUrl || ""
      );

      if (!s3UploadSuccess) {
        setError("Failed to upload PDF to S3. Please try again.");
        return;
      }

      // Download local copy
      console.log("Downloading local copy...");
      downloadPDF(pdfBlob, filename);

      // Prepare form submission data with structured field format
      const structuredFormData =
        formData?.formTemplate.form_schema.sections
          .flatMap((section) => section.fields)
          .filter(
            (field) =>
              formValues[field.name] !== undefined &&
              formValues[field.name] !== null &&
              formValues[field.name] !== ""
          )
          .map((field) => ({
            fieldName: field.name, // Use field.name from formTemplate instead of field.label
            submittedValue: formValues[field.name],
          })) || [];

      const submissionData = {
        formData: structuredFormData,
        signature: signature,
        formType: formData?.formTemplate.form_type,
        locationId: formData?.intakeToken.location_id,
        patientId: formData?.intakeToken.patient_id,
        s3Key: formData?.s3Key,
        pdfGenerated: true,
        pdfFilename: filename,
      };

      // Submit form data to backend
      console.log("Submitting form data to backend...");
      console.log(
        "Submission URL:",
        buildApiUrl(`${API_CONFIG.ENDPOINTS.INTAKE_FORM}/${token}/submit`)
      );
      console.log("Submission data:", submissionData);


      // Try to submit to backend, but don't block the user if it fails
      let backendSuccess = false;
      try {
        const response = await axios.post(
          buildApiUrl(`${API_CONFIG.ENDPOINTS.INTAKE_FORM}/${token}/submit`),
          submissionData,
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
              "Content-Type": "application/json",
            },
            timeout: 10000, // 10 second timeout
          }
        );

        console.log("Backend response:", response.data);
        backendSuccess = response.data.success;
      } catch (backendError: any) {
        console.warn(
          "Backend submission failed, but continuing with redirect:",
          backendError.message
        );
        backendSuccess = false;
      }

      // Proceed with success regardless of backend response since PDF is already uploaded
      console.log("Form processing completed, proceeding with redirect");
      setSuccess(true);
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (error: any) {
      console.error("Error in form submission process:", error);

      // Even if there's an error, proceed with redirect since PDF is uploaded
      console.log("Proceeding with redirect despite error");
      setSuccess(true);
      setTimeout(() => {
        onComplete();
      }, 1500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.id,
      name: field.name,
      value: formValues[field.name] || "",
      onChange: (
        e: React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      ) => handleInputChange(field.name, e.target.value),
      required: field.required,
      style: {
        width: "100%",
        padding: "0.75rem",
        border: "1px solid #ddd",
        borderRadius: "4px",
        fontSize: "1rem",
        fontFamily: brandingInfo?.branding?.fontFamily || "Inter, sans-serif",
      },
    };

    switch (field.type) {
      case "text":
      case "email":
      case "tel":
        return (
          <input {...commonProps} type={field.type} placeholder={field.label} />
        );

      case "date":
        return (
          <input
            {...commonProps}
            type="date"
            max={field.validation?.maxDate}
            min={field.validation?.minDate}
          />
        );

      case "textarea":
        return (
          <textarea
            {...commonProps}
            rows={4}
            placeholder={field.description || field.label}
            maxLength={field.validation?.maxLength}
          />
        );

      case "select":
        return (
          <select {...commonProps}>
            <option value="">Select {field.label}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "checkbox":
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              type="checkbox"
              id={field.id}
              name={field.name}
              checked={formValues[field.name] || false}
              onChange={(e) => handleInputChange(field.name, e.target.checked)}
              required={field.required}
            />
            <label htmlFor={field.id} style={{ margin: 0 }}>
              {field.label}
            </label>
          </div>
        );

      default:
        return <input {...commonProps} type="text" placeholder={field.label} />;
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: brandingInfo?.branding?.fontFamily || "Inter, sans-serif",
        }}
      >
        <div>Loading form...</div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: brandingInfo?.branding?.fontFamily || "Inter, sans-serif",
          color: "#d32f2f",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h2>Error</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              backgroundColor: "#2c5aa0",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: brandingInfo?.branding?.fontFamily || "Inter, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✅</div>
          <h2 style={{ color: "#28a745", marginBottom: "1rem" }}>
            Form Submitted Successfully!
          </h2>
          <p style={{ color: "#666", marginBottom: "1rem" }}>
            Thank you for completing the {formData?.formTemplate.template_name}.
          </p>
          <p style={{ color: "#666", fontSize: "0.9rem" }}>
            Redirecting you back to the form progress screen...
          </p>
          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #28a745",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  const primaryColor = brandingInfo?.branding?.primaryColor || "#2c5aa0";
  const secondaryColor = brandingInfo?.branding?.secondaryColor || "#f8f9fa";
  const fontFamily = brandingInfo?.branding?.fontFamily || "Inter, sans-serif";

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: secondaryColor,
        fontFamily: fontFamily,
      }}
    >
      {/* Add CSS for loading spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Header */}
      <header
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
          color: "white",
          padding: "2rem",
          textAlign: "center",
          position: "relative",
        }}
      >
        {brandingInfo?.branding?.logoUrl && (
          <img
            src={brandingInfo.branding.logoUrl}
            alt="Logo"
            style={{
              height: "60px",
              marginBottom: "1rem",
              borderRadius: "8px",
            }}
          />
        )}
        <h1 style={{ margin: 0, fontSize: "2rem" }}>
          {formData.formTemplate.template_name}
        </h1>
        <p style={{ margin: "0.5rem 0 0 0", opacity: 0.9 }}>
          {formData.formTemplate.description}
        </p>
      </header>

      {/* Main Content */}
      <main
        style={{
          maxWidth: "800px",
          margin: "3rem auto",
          padding: "0 2rem",
        }}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "2rem",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            {formData.formTemplate.form_schema.sections
              .sort((a, b) => a.order - b.order)
              .map((section) => (
                <div key={section.id} style={{ marginBottom: "2rem" }}>
                  <h2
                    style={{
                      color: primaryColor,
                      marginBottom: "1rem",
                      borderBottom: `2px solid ${primaryColor}20`,
                      paddingBottom: "0.5rem",
                    }}
                  >
                    {section.title}
                  </h2>

                  {section.description && (
                    <p
                      style={{
                        color: "#666",
                        marginBottom: "1.5rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {section.description}
                    </p>
                  )}

                  {section.content?.introduction && (
                    <div
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "1rem",
                        borderRadius: "8px",
                        marginBottom: "1.5rem",
                      }}
                    >
                      <p style={{ margin: 0, color: "#666" }}>
                        {section.content.introduction}
                      </p>
                    </div>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gap: "1rem",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(300px, 1fr))",
                    }}
                  >
                    {section.fields
                      .sort((a, b) => a.order - b.order)
                      .map((field) => (
                        <div key={field.id} style={{ marginBottom: "1rem" }}>
                          <label
                            htmlFor={field.id}
                            style={{
                              display: "block",
                              marginBottom: "0.5rem",
                              fontWeight: "500",
                              color: "#333",
                            }}
                          >
                            {field.label}
                            {field.required && (
                              <span
                                style={{
                                  color: "#d32f2f",
                                  marginLeft: "0.25rem",
                                }}
                              >
                                *
                              </span>
                            )}
                          </label>

                          {field.description && (
                            <p
                              style={{
                                fontSize: "0.875rem",
                                color: "#666",
                                marginBottom: "0.5rem",
                                marginTop: 0,
                              }}
                            >
                              {field.description}
                            </p>
                          )}

                          {renderField(field)}
                        </div>
                      ))}
                  </div>

                  {section.privacyNotice && (
                    <div
                      style={{
                        backgroundColor: "#e3f2fd",
                        padding: "1rem",
                        borderRadius: "8px",
                        marginTop: "1rem",
                        borderLeft: `4px solid ${primaryColor}`,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.875rem",
                          color: "#1976d2",
                        }}
                      >
                        <strong>Privacy Notice:</strong> {section.privacyNotice}
                      </p>
                    </div>
                  )}
                </div>
              ))}

            {/* Signature Section */}
            {formData.formTemplate.form_schema.metadata?.signatureRequired && (
              <div style={{ marginBottom: "2rem" }}>
                <h2
                  style={{
                    color: primaryColor,
                    marginBottom: "1rem",
                    borderBottom: `2px solid ${primaryColor}20`,
                    paddingBottom: "0.5rem",
                  }}
                >
                  Digital Signature
                </h2>

                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "1rem",
                    borderRadius: "8px",
                    marginBottom: "1.5rem",
                  }}
                >
                  <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
                    <strong>Legal Notice:</strong> By signing below, you
                    acknowledge that you have read, understood, and agree to the
                    terms and conditions outlined in this form. Your digital
                    signature has the same legal effect as a handwritten
                    signature.
                  </p>
                </div>

                <SignaturePad
                  onSignatureChange={setSignature}
                  width={400}
                  height={200}
                  backgroundColor="#ffffff"
                  penColor="#000000"
                />
              </div>
            )}

            {/* Form Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "2rem",
                paddingTop: "2rem",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  // Check if form has unsaved data
                  const hasFormData = Object.values(formValues).some(
                    (value) =>
                      value !== undefined && value !== null && value !== ""
                  );

                  if (hasFormData) {
                    const confirmLeave = window.confirm(
                      "You have unsaved changes. Are you sure you want to go back to the form selection?"
                    );
                    if (confirmLeave) {
                      onComplete();
                    }
                  } else {
                    onComplete();
                  }
                }}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "transparent",
                  color: primaryColor,
                  border: `2px solid ${primaryColor}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.color = "white";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = primaryColor;
                }}
              >
                Back
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  padding: "0.75rem 2rem",
                  backgroundColor: isSubmitting ? "#ccc" : primaryColor,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontWeight: "500",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                {isSubmitting ? "Submitting..." : "Submit Form"}
              </button>
            </div>

            {error && (
              <div
                style={{
                  backgroundColor: "#ffebee",
                  color: "#d32f2f",
                  padding: "1rem",
                  borderRadius: "8px",
                  marginTop: "1rem",
                  border: "1px solid #ffcdd2",
                }}
              >
                {error}
              </div>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default DynamicForm;
